import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import useSEO from '../hooks/useSEO';
import RelatedSolutions from '../components/RelatedSolutions';
import CrossSellBlock from '../components/CrossSellBlock';

const { 
  FiZap, FiCpu, FiMessageSquare, FiTarget, FiCheckCircle, FiArrowRight, 
  FiInstagram, FiXCircle, FiTrendingUp, FiShield, FiArrowDown, 
  FiCalendar, FiClock, FiActivity, FiUserCheck, FiRepeat, FiUserX, FiSlash, FiMessageCircle 
} = FiIcons;

const BeautyClinicOffer = () => {
  const { t, lang } = useLanguage();
  const content = t.beautyClinic;
  const INSTAGRAM_URL = "https://www.instagram.com/mathiasmazeedeppe/";
  const TIDYCAL_URL = "https://tidycal.com/mathiasdeppe/free-15-minute-growth-review";

  const problemIcons = [FiClock, FiRepeat, FiUserX, FiMessageCircle];
  const howIcons = [FiZap, FiActivity, FiMessageSquare, FiTarget, FiCalendar, FiMessageCircle];
  const trustIcons = [FiMessageSquare, FiTrendingUp, FiCalendar, FiUserCheck, FiZap, FiMessageCircle];

  const pivotUpdates = {
    en: {
      postCommentsTitle: "Post Comments",
      postCommentsText: "Turn public comments into private conversations. The AI replies to the comment and moves the prospect into your DMs.",
      commentToDmTitle: "Comment to DM",
      commentToDmDesc: "Tell your audience to comment a trigger word. The AI instantly replies to their comment publicly and sends a DM to start the conversation.",
      commentToDmShort: "Comment to DM automation"
    },
    es: {
      postCommentsTitle: "Comentarios en Posts",
      postCommentsText: "Convierte comentarios públicos en conversaciones privadas. La IA responde al comentario y lleva al prospecto a tus DMs.",
      commentToDmTitle: "De Comentario a DM",
      commentToDmDesc: "Pide a tu audiencia que comente una palabra clave. La IA responde al instante a su comentario públicamente y les envía un DM para iniciar la conversación.",
      commentToDmShort: "Automatización de Comentario a DM"
    }
  };

  const p_pivot = pivotUpdates[lang] || pivotUpdates.en;

  const dynamicPricing = {
    en: {
      heroPricing: "€99/mo + 10% of attributed first-treatment revenue | No new salesperson. No complicated CRM. No ad spend required.",
      mainPrice: "€99/mo + 10% first-treatment revenue"
    },
    es: {
      heroPricing: "99 €/mes + 10 % de los ingresos atribuidos del primer tratamiento | Sin nuevos comerciales. Sin CRMs complejos. Sin necesidad de inversión publicitaria.",
      mainPrice: "99 €/mes + 10 % ingresos primer tratamiento"
    }
  };

  const p = dynamicPricing[lang] || dynamicPricing.en;

  useSEO(
    "Beauty Clinic Marketing & AI Lead Automation | Barcelona | Mathias Deppe",
    "Turn more Instagram enquiries into beauty consultations and appointments with an AI lead assistant built for aesthetic clinics."
  );

  const relatedLinks = [
    { 
      path: "/ai-lead-agent", 
      label: lang === 'es' ? "Asistente de Leads con IA" : "AI Lead Assistant",
      description: lang === 'es' ? "Cualifica prospectos y gestiona reservas en piloto automático." : "Qualify prospects and manage bookings on autopilot."
    },
    { 
      path: "/fitness-offer", 
      label: lang === 'es' ? "Soluciones para Gimnasios" : "Gym Solutions",
      description: lang === 'es' ? "Inyección de efectivo y captación de leads para centros fitness." : "Cash injection and lead capture for fitness centers."
    }
  ];

  return (
    <div className="bg-zinc-950 text-white min-h-screen selection:bg-orange-500/30">
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-orange-500/10 blur-[120px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-6 uppercase"
          >
            {content.hero.h1}
          </motion.h1>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl md:text-2xl font-bold text-zinc-300 mb-8 tracking-tight max-w-4xl mx-auto"
          >
            {content.hero.h2}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto text-zinc-400 text-lg md:text-xl leading-relaxed mb-8"
          >
            {content.hero.body}
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-block px-6 py-3 bg-zinc-900 border border-orange-500/30 rounded-2xl mb-12 text-orange-500 font-black tracking-widest uppercase text-sm"
          >
            {p.heroPricing}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-white text-zinc-950 font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl">
              {content.hero.cta}
            </a>
          </motion.div>
        </div>
      </section>

      {/* The Problem Grid */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tighter uppercase">{content.problem.h2}</h2>
            <p className="text-xl text-zinc-500">{content.problem.subtext}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {content.problem.items.map((item, i) => (
              <div key={i} className="p-8 bg-zinc-950 border border-white/5 rounded-3xl hover:border-red-500/20 transition-all">
                <SafeIcon icon={problemIcons[i] || FiXCircle} className="text-red-500 text-3xl mb-6" />
                <h3 className="text-xl font-bold mb-4 text-white">
                  {i === 3 ? p_pivot.postCommentsTitle : item.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed text-sm">
                  {i === 3 ? p_pivot.postCommentsText : item.desc}
                </p>
              </div>
            ))}
          </div>
          <div className="p-8 bg-orange-500/10 border-2 border-orange-500/30 rounded-[2rem] text-center">
            <p className="text-xl md:text-2xl font-black text-orange-500 uppercase tracking-tighter">
              {content.problem.closing}
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.howItWorks.h2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.howItWorks.items.map((item, i) => (
              <motion.div 
                key={i}
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 20 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-8 bg-zinc-900 border border-white/5 rounded-3xl hover:border-orange-500/20 transition-all group"
              >
                <div className="w-14 h-14 bg-zinc-950 rounded-2xl flex items-center justify-center mb-8 border border-white/5 group-hover:border-orange-500/50 transition-colors">
                  <SafeIcon icon={howIcons[i] || FiZap} className="text-2xl text-orange-500" />
                </div>
                <h3 className="text-xl font-bold mb-4 tracking-tight text-white">
                  {i === 5 ? p_pivot.commentToDmTitle : item.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed text-sm">
                  {i === 5 ? p_pivot.commentToDmDesc : item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Journey Flowchart */}
      <section className="py-24 px-6 bg-zinc-900/20 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.journey.h2}
          </h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
            {content.journey.steps.map((step, i) => (
              <React.Fragment key={i}>
                <motion.div 
                  whileInView={{ opacity: 1, scale: 1 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`px-6 py-4 rounded-xl font-black text-xs md:text-sm uppercase tracking-widest text-center min-w-[140px] ${i === content.journey.steps.length - 1 ? 'bg-orange-500 text-white shadow-[0_0_30px_rgba(249,115,22,0.3)]' : 'bg-zinc-900 text-zinc-400 border border-white/5'}`}
                >
                  {step}
                </motion.div>
                {i < content.journey.steps.length - 1 && (
                  <div className="flex items-center justify-center py-2">
                    <SafeIcon icon={FiArrowRight} className="text-orange-500 text-xl hidden md:block" />
                    <SafeIcon icon={FiArrowDown} className="text-orange-500 text-xl md:hidden" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <p className="text-center mt-12 text-zinc-500 italic text-lg">
            {content.journey.subtext}
          </p>
        </div>
      </section>

      {/* Capabilities & Medical Trust */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-black text-center mb-16 tracking-tighter uppercase">
            {content.trust.h2}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {content.trust.cards.map((card, i) => (
              <div key={i} className="p-8 bg-zinc-900 border border-white/5 rounded-3xl group hover:bg-zinc-900/80 transition-colors">
                <div className="w-12 h-12 bg-zinc-950 rounded-xl flex items-center justify-center mb-6 border border-white/5 group-hover:border-orange-500/30 transition-all">
                  <SafeIcon icon={trustIcons[i] || FiMessageSquare} className="text-xl text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {i === 5 ? p_pivot.postCommentsTitle : card.title}
                </h3>
                <p className="text-zinc-500 italic text-sm">
                  "{i === 5 ? p_pivot.postCommentsText : card.desc}"
                </p>
              </div>
            ))}
          </div>
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            className="p-8 md:p-12 bg-zinc-950 border-2 border-orange-500/20 rounded-[3rem] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <SafeIcon icon={FiShield} className="text-6xl text-orange-500" />
            </div>
            <p className="relative z-10 text-orange-500 font-bold text-lg leading-relaxed text-center max-w-4xl mx-auto">
              {content.trust.medical}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Positioning */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            whileInView={{ opacity: 1, scale: 1 }}
            initial={{ opacity: 0, scale: 0.95 }}
            viewport={{ once: true }}
            className="p-12 md:p-16 bg-zinc-900 border-2 border-orange-500/30 rounded-[3rem] relative overflow-hidden text-center"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 blur-[100px] pointer-events-none" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter uppercase text-white">{content.positioning.h2}</h2>
              <p className="text-xl md:text-2xl text-white font-bold leading-relaxed mb-12">
                {content.positioning.text}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                {content.positioning.logic.map((step, i) => (
                  <React.Fragment key={i}>
                    <span className="px-4 py-2 bg-zinc-950 border border-white/10 rounded-lg text-sm font-black uppercase tracking-widest text-orange-500">
                      {step}
                    </span>
                    {i < content.positioning.logic.length - 1 && (
                      <SafeIcon icon={FiArrowRight} className="text-zinc-600" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Beauty Clinics */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-black mb-8 tracking-tighter uppercase">{content.whyClinics.h2}</h2>
              <ul className="space-y-6">
                {content.whyClinics.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-300 text-lg">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-500 mt-1 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-12 text-xl font-black text-white uppercase tracking-tighter">
                {content.whyClinics.conclusion}
              </p>
            </div>
            <div className="aspect-square rounded-[3rem] bg-zinc-900 border border-white/5 relative overflow-hidden group">
              <div className="absolute inset-0 bg-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute inset-0 flex items-center justify-center">
                <SafeIcon icon={FiTrendingUp} className="text-[12rem] text-orange-500/10 group-hover:scale-110 transition-transform duration-700" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fit: Do's and Don'ts */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-10 bg-zinc-950 border border-green-500/10 rounded-[2.5rem]">
              <h3 className="text-xl font-black text-green-500 uppercase tracking-widest mb-8 flex items-center gap-3">
                <SafeIcon icon={FiCheckCircle} /> {content.fit.do.title}
              </h3>
              <ul className="space-y-6">
                {content.fit.do.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-300">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-2.5 shrink-0" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-10 bg-zinc-950 border border-red-500/10 rounded-[2.5rem] opacity-70 grayscale hover:grayscale-0 transition-all">
              <h3 className="text-xl font-black text-red-500 uppercase tracking-widest mb-8 flex items-center gap-3">
                <SafeIcon icon={FiXCircle} /> {content.fit.dont.title}
              </h3>
              <ul className="space-y-6">
                {content.fit.dont.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-zinc-500">
                    <div className="w-1.5 h-1.5 bg-zinc-700 rounded-full mt-2.5 shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Setup */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 40 }}
            viewport={{ once: true }}
            className="bg-zinc-900 border-2 border-orange-500 p-12 md:p-16 rounded-[4rem] text-center shadow-[0_0_100px_rgba(249,115,22,0.1)] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10">
              <SafeIcon icon={FiZap} className="text-6xl text-orange-500" />
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tighter uppercase text-white">{content.pricing.h2}</h2>
            <div className="text-3xl md:text-5xl font-black text-orange-500 mb-12 tracking-tighter">
              {p.mainPrice}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 text-left max-w-2xl mx-auto mb-12">
              {content.pricing.included.map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-zinc-300 font-bold">
                  <SafeIcon icon={FiCheckCircle} className="text-orange-500 shrink-0" />
                  <span className="text-sm uppercase tracking-widest">
                    {item.toLowerCase().includes('follow-up') ? p_pivot.commentToDmShort : item}
                  </span>
                </div>
              ))}
            </div>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-zinc-950 text-xl font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl group">
              {content.finalCta.cta}
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Implementation Steps */}
      <section className="py-24 px-6 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {content.implementation.steps.map((step, i) => (
              <div key={i} className="text-center group">
                <div className="w-12 h-12 bg-zinc-900 border border-white/5 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:border-orange-500/50 transition-colors">
                  <span className="text-orange-500 font-black">{i + 1}</span>
                </div>
                <h3 className="text-white font-bold mb-2 uppercase tracking-tighter">{step.title}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Proof Section */}
      <section id="demo" className="py-32 px-6 bg-zinc-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-orange-500/5 via-transparent to-transparent opacity-50" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter uppercase text-white">
            {content.proof.h2}
          </h2>
          <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-3xl mx-auto font-medium">
            {content.proof.body}
          </p>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-4 px-12 py-6 bg-white text-zinc-950 text-xl font-black rounded-full hover:bg-orange-500 hover:text-white transition-all transform hover:scale-105 shadow-2xl group">
            {content.proof.cta}
            <SafeIcon icon={FiInstagram} className="group-hover:scale-110 transition-transform" />
          </a>
        </div>
      </section>

      {/* Cross-Sell Loop */}
      <CrossSellBlock variant="beauty" />

      {/* Internal Linking */}
      <RelatedSolutions links={relatedLinks} />

      {/* Final CTA */}
      <section className="py-32 px-6 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 20 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-7xl font-black text-white mb-8 tracking-tighter uppercase">
              {content.finalCta.h2}
            </h2>
            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {content.finalCta.body}
            </p>
            <a href={TIDYCAL_URL} target="_blank" rel="noopener noreferrer" className="px-12 py-6 text-2xl font-black text-zinc-950 bg-white rounded-full hover:bg-orange-500 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-2xl">
              {content.finalCta.cta}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BeautyClinicOffer;